<template>
  <div class="app">
    <header class="app-header">
      <h1>PNG转ICO在线转换器 | 批量PNG转ICO图标生成工具</h1>
      <p class="app-subtitle">纯前端PNG转ICO工具，支持16/32/48/256 px多尺寸ICO批量输出，文件本地处理不上传</p>
    </header>

    <main class="app-main">
      <!-- Section 1: file picker -->
      <section class="card">
        <h2 class="card__title">
          <span class="section-index">1</span>
          选择 PNG 文件
        </h2>
        <PngUploader :max-size-mb="50" @files-selected="onFilesSelected" />
        <div v-if="rejectedNotice.length" class="rejected">
          <div class="rejected__title">以下文件未通过校验：</div>
          <ul class="rejected__list">
            <li v-for="(r, i) in rejectedNotice" :key="i">
              <span class="rejected__name">{{ r.name }}</span>
              <span class="rejected__reason">{{ r.reason }}</span>
            </li>
          </ul>
        </div>
      </section>

      <!-- Section 2: file preview list -->
      <section v-if="files.length" class="card">
        <h2 class="card__title">
          <span class="section-index">2</span>
          文件预览
          <span class="card__hint">共 {{ files.length }} 个</span>
        </h2>
        <FileList :files="files" @remove="removeFile" @clear="clearAll" />
      </section>

      <!-- Section 3: size selection -->
      <section v-if="files.length" class="card">
        <h2 class="card__title">
          <span class="section-index">3</span>
          选择输出尺寸
        </h2>
        <SizeSelector v-model="selectedSizes" />
      </section>

      <!-- Section 4: action -->
      <section v-if="files.length" class="card">
        <h2 class="card__title">
          <span class="section-index">4</span>
          执行转换
        </h2>
        <div class="action-row">
          <button
            class="btn btn-primary"
            :disabled="!canConvert"
            @click="convertAll"
          >
            {{ isConverting ? `转换中 (${progress.done}/${progress.total})` : '开始转换' }}
          </button>
          <span v-if="!selectedSizes.length" class="action-row__hint">
            请至少选择一个输出尺寸
          </span>
          <span v-else-if="isConverting" class="action-row__hint">
            {{ progress.currentName || '' }}
          </span>
          <span v-else class="action-row__hint">
            将为 {{ files.length }} 个文件生成 {{ files.length * selectedSizes.length }} 个 ICO
          </span>
        </div>
      </section>

      <!-- Section 5: results -->
      <section v-if="results.length" class="card">
        <h2 class="card__title">
          <span class="section-index">5</span>
          转换结果
        </h2>
        <ResultList
          :results="results"
          @download="downloadOne"
          @download-zip="downloadZip"
        />
      </section>
    </main>

    <footer class="app-footer">
      &copy;2008-{{ currentYear }} Power by <a href="https://iteeq.com" target="_blank" rel="noopener">iteeq.com</a>
    </footer>
  </div>
</template>

<script>
import PngUploader from './components/PngUploader.vue';
import FileList from './components/FileList.vue';
import SizeSelector from './components/SizeSelector.vue';
import ResultList from './components/ResultList.vue';
import {
  renderPngToSizes,
  buildIcoBlob,
  readFileAsDataUrl,
  readImageDims,
  baseName,
  formatBytes,
} from './utils/pngToIco.js';
import { downloadBlob } from './utils/download.js';
import JSZip from 'jszip';
import { FILE_STATUS } from './constants.js';

let nextId = 1;
const newId = () => `f${nextId++}`;

export default {
  name: 'App',
  components: { PngUploader, FileList, SizeSelector, ResultList },
  data() {
    return {
      files: [],          // { id, file, name, size, preview, width, height, status, error }
      selectedSizes: [16, 32, 48, 256],
      isConverting: false,
      progress: { done: 0, total: 0, currentName: '' },
      results: [],        // { id, name, blobs: [{ size, blob, filename }] }
      rejectedNotice: [],
      currentYear: new Date().getFullYear(),
    };
  },
  computed: {
    canConvert() {
      return (
        !this.isConverting &&
        this.files.length > 0 &&
        this.selectedSizes.length > 0
      );
    },
  },
  methods: {
    formatBytes,
    baseName,

    async onFilesSelected({ accepted, rejected }) {
      this.rejectedNotice = rejected;

      // Generate previews and dimensions in parallel.
      const additions = await Promise.all(
        accepted.map(async (file) => {
          let preview = '';
          let width = 0;
          let height = 0;
          try {
            const [dataUrl, dims] = await Promise.all([
              readFileAsDataUrl(file),
              readImageDims(file).catch(() => ({ width: 0, height: 0 })),
            ]);
            preview = dataUrl;
            width = dims.width;
            height = dims.height;
          } catch (e) {
            // Non-fatal: file may still be convertible, just no preview.
          }
          return {
            id: newId(),
            file,
            name: file.name,
            size: file.size,
            preview,
            width,
            height,
            status: FILE_STATUS.PENDING,
            error: '',
          };
        })
      );
      this.files = [...this.files, ...additions];
    },

    removeFile(id) {
      this.files = this.files.filter((f) => f.id !== id);
      this.results = this.results.filter((r) => r.id !== id);
    },

    clearAll() {
      if (this.isConverting) return;
      this.files = [];
      this.results = [];
      this.rejectedNotice = [];
    },

    async convertAll() {
      if (!this.canConvert) return;
      this.isConverting = true;
      this.progress = { done: 0, total: this.files.length, currentName: '' };
      this.results = [];

      for (const fileRef of this.files) {
        this.progress.currentName = fileRef.name;
        fileRef.status = FILE_STATUS.CONVERTING;
        fileRef.error = '';

        try {
          // eslint-disable-next-line no-await-in-loop
          const entries = await renderPngToSizes(fileRef.file, this.selectedSizes);
          // Build a combined multi-size ICO and per-size single-size ICOs in one pass.
          const combined = buildIcoBlob(entries);
          const perSize = entries.map(({ size, bytes }) => ({
            size,
            blob: buildIcoBlob([{ size, bytes }]),
            filename: `${this.baseName(fileRef.name)}-${size}.ico`,
          }));
          this.results.push({
            id: fileRef.id,
            name: this.baseName(fileRef.name),
            blobs: perSize,
            combined,
          });
          fileRef.status = FILE_STATUS.DONE;
        } catch (e) {
          fileRef.status = FILE_STATUS.ERROR;
          fileRef.error = e && e.message ? e.message : String(e);
        } finally {
          this.progress.done += 1;
        }
      }

      this.isConverting = false;
      this.progress.currentName = '';
    },

    downloadOne(blob, filename) {
      downloadBlob(blob, filename);
    },

    async downloadZip() {
      if (!this.results.length) return;
      const zip = new JSZip();
      for (const r of this.results) {
        for (const b of r.blobs) {
          zip.file(b.filename, b.blob);
        }
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      downloadBlob(blob, `png2ico-${timestamp()}.zip`);
    },
  },
};

function timestamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}
</script>

<style lang="less">
.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-header {
  background: linear-gradient(135deg, #1a1a1a 0%, #2a1a0d 100%);
  color: #fff;
  padding: 20px 24px;
  border-bottom: 2px solid var(--accent);

  h1 {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .app-subtitle {
    font-size: 14px;
    color: var(--text-dim);
    font-weight: 400;
    line-height: 1.6;
    margin: 0;
  }
}

.app-main {
  flex: 1;
  padding: 24px;
  max-width: 960px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card {
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 0;

  &__title {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 14px;
    color: var(--accent);
    display: flex;
    align-items: center;
    gap: 8px;
    letter-spacing: 0.5px;
  }

  &__hint {
    color: var(--text-dim);
    font-size: 12px;
    margin-left: auto;
  }
}

.section-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--accent);
  color: #000;
  font-size: 12px;
  font-weight: 600;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease, transform 0.05s ease;
  border: 1px solid transparent;
  white-space: nowrap;
  cursor: pointer;
  font-family: inherit;

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
}

.btn-primary {
  background: var(--accent);
  color: #000;
  border-color: var(--accent);

  &:hover:not(:disabled) {
    background: var(--accent-hover);
    border-color: var(--accent-hover);
  }
}

.btn-ghost {
  background: transparent;
  color: var(--text-dim);

  &:hover:not(:disabled) {
    background: var(--bg-elev-2);
    color: var(--text);
  }
}

.btn-danger-ghost {
  background: transparent;
  color: var(--error);

  &:hover:not(:disabled) {
    background: rgba(255, 77, 79, 0.12);
  }
}

.rejected {
  margin-top: 14px;
  padding: 10px 12px;
  background: rgba(255, 77, 79, 0.08);
  border: 1px solid rgba(255, 77, 79, 0.3);
  border-radius: 6px;
  font-size: 12px;
  color: var(--error);

  &__title {
    font-weight: 500;
    margin-bottom: 4px;
  }

  &__list {
    margin: 0;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__name {
    font-weight: 500;
    margin-right: 6px;
  }

  &__reason {
    color: var(--error);
  }
}

.action-row {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;

  &__hint {
    font-size: 12px;
    color: var(--text-dim);
  }
}

.app-footer {
  text-align: center;
  padding: 20px 16px;
  color: var(--text-dim);
  font-size: 13px;
  border-top: 1px solid var(--border);

  a {
    color: var(--accent);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

@media (max-width: 900px) {
  .app-header {
    padding: 16px;

    h1 { font-size: 17px; }
    .app-subtitle { font-size: 13px; }
  }
  .app-main {
    padding: 16px;
  }
}
</style>
